// ============================================
// ConverseTech Frontend - Audio Capture Hook
// ============================================

import { useState, useRef, useCallback } from 'react';
import { AUDIO_CONFIG } from '@conversetech/shared';

interface UseAudioCaptureOptions {
  onChunk: (data: string) => void;  // base64 encoded PCM
}

export function useAudioCapture({ onChunk }: UseAudioCaptureOptions) {
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Use ref for muted state so the audio callback always has current value
  const isMutedRef = useRef(false);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);

  const startRecording = useCallback(async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: AUDIO_CONFIG.INPUT_SAMPLE_RATE,
          channelCount: AUDIO_CONFIG.CHANNELS,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      mediaStreamRef.current = stream;

      // Create audio context
      const audioContext = new AudioContext({
        sampleRate: AUDIO_CONFIG.INPUT_SAMPLE_RATE,
      });
      audioContextRef.current = audioContext;

      // Create source from stream
      const source = audioContext.createMediaStreamSource(stream);

      // Create processor node
      // Note: ScriptProcessorNode is deprecated but simpler for this use case
      // In production, use AudioWorklet for better performance
      const processor = audioContext.createScriptProcessor(
        4096, // buffer size
        AUDIO_CONFIG.CHANNELS,
        AUDIO_CONFIG.CHANNELS
      );
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (isMutedRef.current) return;

        const inputData = event.inputBuffer.getChannelData(0);

        // Convert Float32Array to Int16Array (16-bit PCM)
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          // Clamp values and convert to 16-bit
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Convert to base64
        const uint8Array = new Uint8Array(pcmData.buffer);
        const base64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));

        onChunk(base64);
      };

      // Connect nodes
      source.connect(processor);
      processor.connect(audioContext.destination);

      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  }, [onChunk]);

  const stopRecording = useCallback(() => {
    // Stop all tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Disconnect processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsRecording(false);
    setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newValue = !prev;
      isMutedRef.current = newValue;  // Update ref synchronously
      return newValue;
    });
  }, []);

  return {
    isRecording,
    isMuted,
    startRecording,
    stopRecording,
    toggleMute,
  };
}
